import React, { useEffect, useState } from "react";
import { Table, Tag, Tooltip, Progress, Badge } from "antd";
import { EditOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";

const OrderTable = ({ orders, loading, searchKeyword }) => {
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const handleResize = () => setScreenHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const HighlightText = ({ text }) => (
    <Highlighter
      highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
      searchWords={[searchKeyword]}
      autoEscape={true}
      textToHighlight={String(text || '')}
    />
  );

  const columns = [
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      width: 100,
      align: "center",
      render: (id, record) =>
        <div style={{ position: 'relative' }}>
          {!record.viewed && <div style={{ position: 'absolute', left: '5px' }}> <Badge color="volcano" /> </div>}
          <HighlightText text={id} />
        </div>,
    },
    {
      title: "Customer",
      key: "customer",
      width: 180,
      align: "center",
      render: (_, record) => (
        <div style={{ fontSize: 13 }}>
          <HighlightText text={record.customer_name} />
        </div>
      ),
    },
    {
      title: "Order Time",
      key: "orderTime",
      width: 140,
      align: "center",
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          <HighlightText text={`${record.date}, ${record.time}`} />
        </div>
      ),
    },
    {
      title: "Products",
      key: "products",
      align: "center",
      render: (_, record) => {
        const productText = record.products
          .map((p) => `${p.quantity}x ${p.name}`)
          .join(", ");
        const displayText =
          productText.length > 40 ? productText.slice(0, 40) + "..." : productText;
        return (
          <div style={{ fontSize: 13 }}>
            <HighlightText text={displayText} />
          </div>
        );
      },
    },
    {
      title: "Phone",
      key: "phone",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div style={{ fontSize: 13 }}>
          <HighlightText text={record.phone} />
        </div>
      ),
    },
    {
      title: "Payable",
      key: "payable",
      width: 100,
      align: "center",
      render: (_, record) => (
        <div>
          {record.payable}<span style={{ fontSize: '18px' }}>{record.currency}</span>
        </div>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      align: "center",
      render: (_, record) => {
        const statusObj = record.status || { status: "unknown", color: "gray" };
        return (
          <Tag
            color={statusObj.color}
            variant="outlined"
            style={{ minWidth: '75px', textAlign: 'center' }}
          >
            <HighlightText text={statusObj.status} />
          </Tag>
        );
      },
    },
    {
      title: "Score",
      key: "score",
      width: 80,
      align: "center",
      render: (_, record) => {
        const percent = record.score;
        const strokeColor =
          percent >= 80
            ? ["#52c41a", "#73d13d"]
            : percent >= 50
              ? ["#faad14", "#ffd666"]
              : ["#f5222d", "#ff7875"];
        return <Progress type="dashboard" percent={percent} strokeColor={strokeColor} size={40} />;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Edit Order">
          <EditOutlined style={{ cursor: "pointer", fontSize: 16 }} />
        </Tooltip>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={orders.map((o) => ({ ...o, key: o.orderId }))}
      size="middle"
      loading={loading}
      scroll={{ y: screenHeight - 270, x: "max-content" }}
      rowKey="key"
      pagination={{
        current: currentPage,
        pageSize,
        total: orders.length,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "25", "50"],
        onChange: (page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        },
      }}
      rowClassName={() => "order-table-row"}
    />
  );
};

export default OrderTable;